import { useState } from 'react';

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TYPE_OPTIONS = [
  'Behind the Scenes', 'Food Shot', 'Cocktail Feature', 'Team Spotlight',
  'Guest Story', 'Seasonal', 'Event', 'Community', 'Reel Idea', 'Story Series',
];

const TIMING_OPTIONS = ['Morning', 'Afternoon', 'Evening'];

const typeColors = {
  'Behind the Scenes': '#C9A668',
  'Food Shot': '#fb923c',
  'Cocktail Feature': '#c084fc',
  'Team Spotlight': '#60a5fa',
  'Guest Story': '#34d399',
  'Seasonal': '#fbbf24',
  'Event': '#f87171',
  'Community': '#06b6d4',
  'Reel Idea': '#e040fb',
  'Story Series': '#a78bfa',
};

const getDateForDay = (startDate, dayOffset) => {
  const d = new Date(startDate);
  d.setDate(d.getDate() + dayOffset);
  return d.toISOString().split('T')[0];
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
};

export default function ContentCalendar({ audit, onUpdate }) {
  const [generating, setGenerating] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [editingIndex, setEditingIndex] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const calendar = audit.contentCalendar || [];

  const lockedCount = calendar.filter(p => p.locked).length;
  const unlockedCount = calendar.length - lockedCount;

  const startDate = audit.calendarStartDate || calendar[0]?.date || new Date().toISOString().split('T')[0];

  const save = (updated) => onUpdate({ ...audit, contentCalendar: updated });

  const setStartDate = (date) => {
    // Shift all post dates relative to new start
    const oldStart = startDate;
    const diff = Math.round((new Date(date) - new Date(oldStart)) / 86400000);
    const updated = calendar.map(p => {
      if (!p.date) return p;
      return { ...p, date: getDateForDay(p.date, diff) };
    });
    onUpdate({ ...audit, calendarStartDate: date, contentCalendar: updated });
  };

  const toggleLock = (index) => {
    const updated = [...calendar];
    updated[index] = { ...updated[index], locked: !updated[index].locked };
    save(updated);
  };

  const lockAll = () => save(calendar.map(p => ({ ...p, locked: true })));
  const unlockAll = () => save(calendar.map(p => ({ ...p, locked: false })));

  const updatePost = (index, field, value) => {
    const updated = [...calendar];
    updated[index] = { ...updated[index], [field]: value };
    save(updated);
  };

  const updateHashtag = (postIndex, tagIndex, value) => {
    const updated = [...calendar];
    const tags = [...(updated[postIndex].hashtags || [])];
    tags[tagIndex] = value;
    updated[postIndex] = { ...updated[postIndex], hashtags: tags };
    save(updated);
  };

  const addHashtag = (postIndex) => {
    const updated = [...calendar];
    updated[postIndex] = { ...updated[postIndex], hashtags: [...(updated[postIndex].hashtags || []), ''] };
    save(updated);
  };

  const removeHashtag = (postIndex, tagIndex) => {
    const updated = [...calendar];
    updated[postIndex] = { ...updated[postIndex], hashtags: (updated[postIndex].hashtags || []).filter((_, i) => i !== tagIndex) };
    save(updated);
  };

  const deletePost = (index) => {
    save(calendar.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
  };

  const addPost = () => {
    const lastDate = calendar.length > 0 ? calendar[calendar.length - 1].date : startDate;
    const nextDate = lastDate ? getDateForDay(lastDate, 1) : getDateForDay(new Date().toISOString().split('T')[0], calendar.length);
    save([...calendar, { day: calendar.length + 1, date: nextDate, type: 'Food Shot', title: '', caption: '', hashtags: [], timing: 'Morning', pillarLink: '', locked: false }]);
    setEditingIndex(calendar.length);
    setViewMode('list');
  };

  // Add a post to a specific date (from calendar view)
  const addPostToDate = (date) => {
    save([...calendar, { day: calendar.length + 1, date, type: 'Food Shot', title: '', caption: '', hashtags: [], timing: 'Morning', pillarLink: '', locked: false }]);
    setEditingIndex(calendar.length);
    setViewMode('list');
  };

  // Move post: reorder in list or change date when dragged to a day cell
  const movePost = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const updated = [...calendar];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    save(updated);
    setEditingIndex(null);
  };

  // Move a post to a specific date (from calendar drag)
  const movePostToDate = (postIndex, targetDate) => {
    const updated = [...calendar];
    updated[postIndex] = { ...updated[postIndex], date: targetDate };
    save(updated);
    setDragFrom(null);
  };

  const generateCalendar = async () => {
    setGenerating(true);
    try {
      const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
      const intake = audit.intake || {};
      const scores = audit.scores || {};
      const ai = audit.aiResearch || {};

      const locked = calendar.filter(p => p.locked);
      const slotsNeeded = Math.max(30 - locked.length, 1);
      const genStartDate = new Date().toISOString().split('T')[0];

      const lockedContext = locked.length > 0
        ? `\n\nThe client has already locked these days (do NOT repeat them, generate DIFFERENT content for the remaining days):\n${locked.map(p => `- ${p.date || 'Day ' + p.day}: ${p.type} — ${p.title}`).join('\n')}`
        : '';

      const prompt = `You are a hospitality social media strategist for Next Experience Group. Generate ${slotsNeeded} days of Instagram content ideas for this restaurant/bar based on the audit data.

Concept: ${intake.conceptName || 'Unknown'}
City: ${intake.city || 'Unknown'}
Instagram: ${intake.instagram || 'N/A'}
Top Strengths: ${Object.entries(scores).filter(([k,v]) => v?.score >= 7).map(([k,v]) => k.replace(/_/g,' ')).join(', ')}
Areas to Improve: ${Object.entries(scores).filter(([k,v]) => v?.score > 0 && v?.score < 6).map(([k,v]) => k.replace(/_/g,' ')).join(', ')}
Positive Review Themes: ${JSON.stringify(ai.guestSentiment?.combined?.positiveThemes || [])}
Beverage Score: ${scores.beverage_intelligence?.score || 'N/A'}
Menu Score: ${scores.menu_intelligence?.score || 'N/A'}
Executive Diagnostic: ${(ai.executiveDiagnostic || '').substring(0, 200)}${lockedContext}

Each post should:
- Leverage their strengths or address their gaps
- Mix content types: behind-the-scenes, food/drink beauty shots, team spotlights, guest stories, seasonal specials, events
- Include a caption idea and 3-5 hashtags
- Be specific to their concept, not generic

Return ONLY this JSON array (${slotsNeeded} items):
[{"day":1,"type":"Behind the Scenes|Food Shot|Cocktail Feature|Team Spotlight|Guest Story|Seasonal|Event|Community|Reel Idea|Story Series","title":"","caption":"","hashtags":[""],"timing":"Morning|Afternoon|Evening","pillarLink":"which audit pillar this supports"}]`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
        body: JSON.stringify({ model: 'claude-sonnet-4-6', max_tokens: 8000, messages: [{ role: 'user', content: prompt }] }),
      });

      const data = await response.json();
      const text = data.content?.[0]?.text || '';
      const match = text.match(/\[[\s\S]*\]/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        const merged = [];
        let newIndex = 0;
        for (const p of calendar) {
          if (p.locked) {
            merged.push(p);
          } else if (newIndex < parsed.length) {
            merged.push(parsed[newIndex++]);
          }
        }
        while (newIndex < parsed.length) {
          merged.push(parsed[newIndex++]);
        }
        // Assign dates — spread across 30 days, but don't force 1-per-day
        let dayCounter = 0;
        merged.forEach((p, i) => {
          if (!p.date) {
            p.date = getDateForDay(genStartDate, dayCounter);
            dayCounter++;
          }
          p.day = i + 1;
        });
        save(merged);
      }
    } catch (err) {
      console.error('Content calendar generation failed:', err);
    }
    setGenerating(false);
  };

  // Build calendar grid: group posts by date, generate 30 day slots from start
  const buildCalendarGrid = () => {
    const postsByDate = {};
    calendar.forEach((post, i) => {
      const d = post.date || getDateForDay(startDate, i);
      if (!postsByDate[d]) postsByDate[d] = [];
      postsByDate[d].push({ ...post, _index: i });
    });

    // Find date range: 30 days minimum, or extend to cover all posts
    const allDates = Object.keys(postsByDate).sort();
    const gridStart = allDates.length > 0 ? allDates[0] : startDate;
    const lastDate = allDates.length > 0 ? allDates[allDates.length - 1] : getDateForDay(startDate, 29);
    const daysBetween = Math.ceil((new Date(lastDate) - new Date(gridStart)) / 86400000) + 1;
    const totalDays = Math.max(30, daysBetween);

    // Pad start to begin on Monday
    const firstDay = new Date(gridStart + 'T12:00:00').getDay(); // 0=Sun
    const padBefore = firstDay === 0 ? 6 : firstDay - 1; // days to pad to reach Monday

    const days = [];
    for (let i = -padBefore; i < totalDays; i++) {
      const date = getDateForDay(gridStart, i);
      days.push({ date, posts: postsByDate[date] || [], inRange: i >= 0 && i < totalDays });
    }
    // Pad end to complete last week
    while (days.length % 7 !== 0) {
      const date = getDateForDay(gridStart, totalDays + (days.length - totalDays));
      days.push({ date, posts: postsByDate[date] || [], inRange: false });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  };

  const calendarWeeks = viewMode === 'calendar' ? buildCalendarGrid() : [];

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg text-white">30-Day Content Calendar</h3>
          <div className="flex items-center gap-2 mt-1">
            <label className="text-[10px] text-neutral-500">Starts:</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="text-[10px] px-2 py-0.5 rounded bg-neutral-800 text-white border-0 focus:ring-1 focus:ring-brand-gold/30" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {calendar.length > 0 && (
            <>
              <span className="text-[9px] text-neutral-600">
                {lockedCount > 0 && <span className="text-brand-gold">{lockedCount} locked</span>}
                {lockedCount > 0 && unlockedCount > 0 && ' · '}
                {unlockedCount > 0 && `${unlockedCount} unlocked`}
              </span>
              <button onClick={lockedCount === calendar.length ? unlockAll : lockAll} className="text-[9px] text-neutral-600 hover:text-neutral-400">
                {lockedCount === calendar.length ? 'Unlock All' : 'Lock All'}
              </button>
              <div className="flex bg-neutral-800 rounded-lg p-0.5">
                <button onClick={() => setViewMode('list')} className={`text-[10px] px-2 py-1 rounded ${viewMode === 'list' ? 'bg-brand-gold/20 text-brand-gold' : 'text-neutral-500'}`}>List</button>
                <button onClick={() => setViewMode('calendar')} className={`text-[10px] px-2 py-1 rounded ${viewMode === 'calendar' ? 'bg-brand-gold/20 text-brand-gold' : 'text-neutral-500'}`}>Calendar</button>
              </div>
            </>
          )}
          <button onClick={addPost} className="btn-ghost text-xs">+ Add</button>
          <button onClick={generateCalendar} disabled={generating} className="btn-primary text-xs py-2 disabled:opacity-50">
            {generating ? 'Generating...' : lockedCount > 0 && unlockedCount > 0 ? `Regenerate ${unlockedCount}` : calendar.length > 0 ? 'Regenerate' : 'Generate Calendar'}
          </button>
        </div>
      </div>

      {calendar.length > 0 ? (
        viewMode === 'list' ? (
          /* ===== LIST VIEW ===== */
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {calendar.map((post, i) => (
              <div key={i}
                draggable
                onDragStart={() => setDragFrom(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragFrom !== null) movePost(dragFrom, i); setDragFrom(null); }}
                className={`rounded-lg p-3 transition-all ${post.locked ? 'bg-neutral-800/30 border border-brand-gold/30' : 'bg-neutral-800/20'} ${dragFrom === i ? 'opacity-50' : ''}`}>
                {editingIndex === i ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-700 cursor-grab text-xs" title="Drag to reorder">⠿</span>
                      <button onClick={() => toggleLock(i)} className={`text-sm flex-shrink-0 ${post.locked ? 'text-brand-gold' : 'text-neutral-700 hover:text-neutral-400'}`}>
                        {post.locked ? '🔒' : '🔓'}
                      </button>
                      <input type="date" value={post.date || ''} onChange={e => updatePost(i, 'date', e.target.value)}
                        className="text-[10px] px-2 py-1 rounded bg-neutral-800 text-white border-0 focus:ring-1 focus:ring-brand-gold/30" />
                      <select value={post.type || ''} onChange={e => updatePost(i, 'type', e.target.value)}
                        className="text-[10px] px-2 py-1 rounded font-medium border-0 bg-neutral-800 text-white flex-1"
                        style={{ color: typeColors[post.type] || '#fff' }}>
                        {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <select value={post.timing || ''} onChange={e => updatePost(i, 'timing', e.target.value)}
                        className="text-[10px] px-2 py-1 rounded bg-neutral-800 text-neutral-300 border-0">
                        {TIMING_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <button onClick={() => setEditingIndex(null)} className="text-[9px] text-brand-gold">Done</button>
                      <button onClick={() => deletePost(i)} className="text-[9px] text-neutral-600 hover:text-red-400">✕</button>
                    </div>
                    <input type="text" value={post.title || ''} onChange={e => updatePost(i, 'title', e.target.value)}
                      placeholder="Post title..." className="input-field text-xs py-1.5" />
                    <textarea value={post.caption || ''} onChange={e => updatePost(i, 'caption', e.target.value)}
                      placeholder="Caption..." rows={2} className="input-field text-[10px]" />
                    <input type="text" value={post.pillarLink || ''} onChange={e => updatePost(i, 'pillarLink', e.target.value)}
                      placeholder="Pillar link..." className="input-field text-[10px] py-1" />
                    <div className="flex flex-wrap items-center gap-1">
                      {(post.hashtags || []).map((tag, j) => (
                        <div key={j} className="flex items-center gap-0.5">
                          <span className="text-[9px] text-brand-gold/60">#</span>
                          <input type="text" value={tag.replace('#', '')} onChange={e => updateHashtag(i, j, e.target.value)}
                            className="bg-transparent text-[9px] text-brand-gold/60 border-b border-neutral-700 focus:border-brand-gold outline-none w-20 py-0.5" />
                          <button onClick={() => removeHashtag(i, j)} className="text-[8px] text-neutral-700 hover:text-red-400">×</button>
                        </div>
                      ))}
                      <button onClick={() => addHashtag(i)} className="text-[8px] text-neutral-600 hover:text-neutral-400">+ tag</button>
                    </div>
                    <div className="flex items-center gap-2 pt-1 border-t border-neutral-800">
                      <span className="text-[9px] text-neutral-600">Move:</span>
                      <button disabled={i === 0} onClick={() => movePost(i, i - 1)} className="text-[9px] text-neutral-500 hover:text-white disabled:opacity-30">↑</button>
                      <button disabled={i === calendar.length - 1} onClick={() => movePost(i, i + 1)} className="text-[9px] text-neutral-500 hover:text-white disabled:opacity-30">↓</button>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => setEditingIndex(i)} className="cursor-pointer hover:bg-neutral-800/30 -m-3 p-3 rounded-lg transition-colors">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-neutral-700 cursor-grab text-xs">⠿</span>
                      <button onClick={(e) => { e.stopPropagation(); toggleLock(i); }} className={`text-sm flex-shrink-0 ${post.locked ? 'text-brand-gold' : 'text-neutral-700 hover:text-neutral-400'}`}>
                        {post.locked ? '🔒' : '🔓'}
                      </button>
                      <span className="text-[10px] font-semibold text-brand-gold w-24">{post.date ? formatDateShort(post.date) : `Day ${post.day || i + 1}`}</span>
                      <span className="text-[9px] px-2 py-0.5 rounded font-medium" style={{ backgroundColor: (typeColors[post.type] || '#666') + '20', color: typeColors[post.type] || '#666' }}>{post.type}</span>
                      <span className="text-[9px] text-neutral-600">{post.timing}</span>
                      {post.pillarLink && <span className="text-[8px] text-neutral-700 ml-auto">{post.pillarLink}</span>}
                    </div>
                    <p className="text-xs text-white font-medium mb-1 ml-12">{post.title}</p>
                    <p className="text-[10px] text-neutral-400 leading-relaxed ml-12">{post.caption}</p>
                    {post.hashtags?.filter(Boolean).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5 ml-12">
                        {post.hashtags.filter(Boolean).map((tag, j) => (
                          <span key={j} className="text-[9px] text-brand-gold/60">#{tag.replace('#', '')}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* ===== CALENDAR GRID VIEW ===== */
          <div className="space-y-1">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DAYS_OF_WEEK.map(d => (
                <div key={d} className="text-[9px] text-neutral-600 text-center font-medium">{d}</div>
              ))}
            </div>
            {calendarWeeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map((day, di) => {
                  const dateNum = new Date(day.date + 'T12:00:00').getDate();
                  const monthLabel = dateNum === 1 || (wi === 0 && di === 0)
                    ? new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short' }) + ' '
                    : '';
                  const isToday = day.date === new Date().toISOString().split('T')[0];
                  return (
                    <div key={di}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-1', 'ring-brand-gold/50'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('ring-1', 'ring-brand-gold/50'); }}
                      onDrop={(e) => { e.currentTarget.classList.remove('ring-1', 'ring-brand-gold/50'); if (dragFrom !== null) { movePostToDate(dragFrom, day.date); } }}
                      className={`rounded min-h-[72px] p-1 transition-colors ${day.inRange ? 'bg-neutral-800/30' : 'bg-neutral-900/20 opacity-40'} ${isToday ? 'ring-1 ring-brand-gold/30' : ''}`}>
                      {/* Date header + add button */}
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-[8px] ${isToday ? 'text-brand-gold font-bold' : 'text-neutral-500'}`}>{monthLabel}{dateNum}</span>
                        {day.inRange && (
                          <button onClick={() => addPostToDate(day.date)} className="text-[8px] text-neutral-700 hover:text-neutral-400" title="Add post to this day">+</button>
                        )}
                      </div>
                      {/* Posts stacked in this day */}
                      <div className="space-y-0.5">
                        {day.posts.map((post) => (
                          <div key={post._index}
                            draggable
                            onDragStart={(e) => { e.stopPropagation(); setDragFrom(post._index); }}
                            onClick={() => { setEditingIndex(post._index); setViewMode('list'); }}
                            className={`rounded px-1 py-0.5 cursor-grab hover:brightness-125 transition-all ${dragFrom === post._index ? 'opacity-40' : ''} ${post.locked ? 'ring-1 ring-brand-gold/40' : ''}`}
                            style={{ backgroundColor: (typeColors[post.type] || '#666') + '25' }}
                            title={`${post.locked ? '🔒 ' : ''}${post.title}\n${post.caption || ''}`}>
                            <div className="flex items-center gap-0.5">
                              {post.locked && <span className="text-[6px]">🔒</span>}
                              <p className="text-[7px] font-medium leading-tight truncate" style={{ color: typeColors[post.type] || '#999' }}>{post.title || 'Untitled'}</p>
                            </div>
                            <p className="text-[6px] text-neutral-600 truncate">{post.type}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )
      ) : (
        <p className="text-neutral-600 text-sm text-center py-8">Click "Generate Calendar" or "+ Add" to build your content strategy.</p>
      )}

      {/* Content type legend */}
      {calendar.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-neutral-800">
          {Object.entries(typeColors).map(([type, color]) => {
            const count = calendar.filter(p => p.type === type).length;
            if (count === 0) return null;
            return (
              <span key={type} className="flex items-center gap-1 text-[9px]" style={{ color }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />{type} ({count})
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
